import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Search, X } from 'lucide-react';
import type { EnhancedDeal, ProposalNodeSet } from '../../types';
import {
  listProposalCompareTargets,
  loadProposalNodeSets,
  type ProposalCompareTarget,
} from '../../utils/proposalStructureApi';
import type { StructureCompareSession } from '../../utils/structureLineCompare';
import './StructureComparePicker.css';

function statusLabel(status?: string): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'sent':
      return 'Sent';
    case 'opened':
    case 'viewed':
      return 'Opened';
    default:
      return 'Draft';
  }
}

type CompareSource = 'local' | 'remote';

interface StructureComparePickerProps {
  currentDealId: string;
  nodeSets: ProposalNodeSet[];
  defaultLeftId: string | null;
  deals: EnhancedDeal[];
  anchorRef?: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onApply: (session: StructureCompareSession) => void;
  onComparePages?: () => void;
}

const StructureComparePicker: React.FC<StructureComparePickerProps> = ({
  currentDealId,
  nodeSets,
  defaultLeftId,
  deals,
  anchorRef,
  onClose,
  onApply,
  onComparePages,
}) => {
  const [pos, setPos] = useState({ top: 72, left: 24 });
  const [leftId, setLeftId] = useState(
    () => defaultLeftId && nodeSets.some(ns => ns.id === defaultLeftId)
      ? defaultLeftId
      : nodeSets[0]?.id || '',
  );
  const [source, setSource] = useState<CompareSource>('local');
  const [rightLocalId, setRightLocalId] = useState(() => {
    const firstOther = nodeSets.find(ns => ns.id !== (defaultLeftId || nodeSets[0]?.id));
    return firstOther?.id || '';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [remoteNodeSets, setRemoteNodeSets] = useState<ProposalNodeSet[]>([]);
  const [remoteRightId, setRemoteRightId] = useState('');
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const [targets, setTargets] = useState<ProposalCompareTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);

  useLayoutEffect(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 320;
    setPos({
      top: rect.bottom + 8,
      left: Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)),
    });
  }, [anchorRef]);

  const leftOthers = useMemo(
    () => nodeSets.filter(ns => ns.id !== leftId),
    [nodeSets, leftId],
  );

  useEffect(() => {
    if (rightLocalId && rightLocalId === leftId) {
      setRightLocalId(leftOthers[0]?.id || '');
    }
  }, [leftId, leftOthers, rightLocalId]);

  useEffect(() => {
    if (source !== 'remote') return;
    let cancelled = false;
    setLoadingTargets(true);
    listProposalCompareTargets()
      .then(list => {
        if (!cancelled) setTargets(list);
      })
      .catch(() => {
        if (cancelled) return;
        setTargets(
          deals
            .filter(d => d.id && d.id !== currentDealId && d.outcome !== 'lost')
            .map(d => ({
              id: d.id as string,
              dealId: d.id as string,
              label: d.title || 'Untitled',
              subtitle: d.contactName || '',
              status: undefined,
            })),
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingTargets(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  const availableTargets = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return targets.filter(t => {
      if (t.dealId === currentDealId || t.id === currentDealId) return false;
      if (!q) return true;
      return (
        t.label.toLowerCase().includes(q) ||
        t.subtitle.toLowerCase().includes(q)
      );
    });
  }, [targets, searchQuery, currentDealId]);

  const selectedTarget = targets.find(t => t.dealId === selectedDealId || t.id === selectedDealId);
  const selectedDeal = deals.find(d => d.id === selectedDealId);

  useEffect(() => {
    if (!selectedDealId) {
      setRemoteNodeSets([]);
      setRemoteRightId('');
      setRemoteError(null);
      return;
    }
    let cancelled = false;
    setLoadingRemote(true);
    setRemoteError(null);
    loadProposalNodeSets(selectedDealId)
      .then(loaded => {
        if (cancelled) return;
        setRemoteNodeSets(loaded);
        setRemoteRightId(loaded[0]?.id || '');
        if (loaded.length === 0) {
          setRemoteError('That proposal has no structures.');
        }
      })
      .catch(() => {
        if (!cancelled) setRemoteError('Could not load that proposal.');
      })
      .finally(() => {
        if (!cancelled) setLoadingRemote(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDealId]);

  const canApply = source === 'local'
    ? Boolean(leftId && rightLocalId && leftId !== rightLocalId)
    : Boolean(leftId && selectedDealId && remoteRightId);

  const handleApply = () => {
    if (!canApply) return;
    if (source === 'local') {
      onApply({ leftNodeSetId: leftId, right: { source: 'local', nodeSetId: rightLocalId } });
      return;
    }
    const nodeSet = remoteNodeSets.find(ns => ns.id === remoteRightId);
    if (!nodeSet || !selectedDealId) return;
    onApply({
      leftNodeSetId: leftId,
      right: {
        source: 'remote',
        dealId: selectedDealId,
        dealTitle: selectedTarget?.label || selectedDeal?.title || 'Other proposal',
        nodeSet,
      },
    });
  };

  return createPortal(
    <div className="scp-backdrop" onMouseDown={onClose}>
    <div
      className="scp-popover"
      role="dialog"
      aria-label="Compare structures"
      style={{ top: pos.top, left: pos.left }}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="scp-header">
        <strong>Compare structures</strong>
        <button type="button" className="scp-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
      </div>

      <label className="scp-label" htmlFor="scp-left">This structure</label>
      <select
        id="scp-left"
        className="scp-select"
        value={leftId}
        onChange={e => setLeftId(e.target.value)}
      >
        {nodeSets.map(ns => (
          <option key={ns.id} value={ns.id}>{ns.name}</option>
        ))}
      </select>

      <div className="scp-source" role="tablist" aria-label="Compare to source">
        <button
          type="button"
          className={`scp-source-btn ${source === 'local' ? 'active' : ''}`}
          onClick={() => setSource('local')}
        >
          This proposal
        </button>
        <button
          type="button"
          className={`scp-source-btn ${source === 'remote' ? 'active' : ''}`}
          onClick={() => setSource('remote')}
        >
          Another proposal
        </button>
      </div>

      {source === 'local' ? (
        <>
          <label className="scp-label" htmlFor="scp-right">Compare to</label>
          {leftOthers.length === 0 ? (
            <p className="scp-empty">Add another structure on this proposal to compare.</p>
          ) : (
            <select
              id="scp-right"
              className="scp-select"
              value={rightLocalId}
              onChange={e => setRightLocalId(e.target.value)}
            >
              {leftOthers.map(ns => (
                <option key={ns.id} value={ns.id}>{ns.name}</option>
              ))}
            </select>
          )}
        </>
      ) : (
        <>
          <label className="scp-label" htmlFor="scp-deal-search">Proposal</label>
          <div className="scp-search">
            <Search size={14} />
            <input
              id="scp-deal-search"
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search client, project, or slug…"
              autoFocus
            />
          </div>
          <div className="scp-deal-list">
            {loadingTargets && (
              <div className="scp-empty"><Loader2 size={14} className="scp-spin" /> Loading proposals…</div>
            )}
            {!loadingTargets && availableTargets.slice(0, 40).map(target => (
              <button
                key={target.id}
                type="button"
                className={`scp-deal-item ${selectedDealId === target.dealId || selectedDealId === target.id ? 'selected' : ''}`}
                onClick={() => setSelectedDealId(target.dealId || target.id)}
              >
                <span className="scp-deal-title">{target.label}</span>
                <span className="scp-deal-contact">
                  {target.subtitle}
                  {target.status ? ` · ${statusLabel(target.status)}` : ''}
                </span>
              </button>
            ))}
            {!loadingTargets && availableTargets.length === 0 && (
              <div className="scp-empty">No matching proposals</div>
            )}
          </div>
          {loadingRemote && (
            <p className="scp-status"><Loader2 size={14} className="scp-spin" /> Loading structures…</p>
          )}
          {remoteError && <p className="scp-empty">{remoteError}</p>}
          {selectedDealId && remoteNodeSets.length > 0 && (
            <>
              <label className="scp-label" htmlFor="scp-remote-right">Structure</label>
              <select
                id="scp-remote-right"
                className="scp-select"
                value={remoteRightId}
                onChange={e => setRemoteRightId(e.target.value)}
              >
                {remoteNodeSets.map(ns => (
                  <option key={ns.id} value={ns.id}>{ns.name}</option>
                ))}
              </select>
            </>
          )}
        </>
      )}

      {onComparePages && (
        <button
          type="button"
          className="scp-pages"
          onClick={onComparePages}
        >
          Compare pages
        </button>
      )}
      <button
        type="button"
        className="scp-apply"
        disabled={!canApply}
        onClick={handleApply}
      >
        Compare structures
      </button>
    </div>
    </div>,
    document.body,
  );
};

export default StructureComparePicker;
